/**
 * The ch02 feature manifest — pre-season planning and the seasonal buy.
 *
 * Eight routes. The four `assortment…` routes are the app «Surtido» (bata-platform#940, `docs/adr/9082`, `docs/adr/4001`),
 * the only ones a planner is offered: the rail draws it inside «Próximas ampliaciones», usable. The other four are the
 * chapter's first pages and are disabled ONE BY ONE (not the chapter): they are not drawn beside the app.
 * Their split is reqs/02's own decision chain rather than a menu:
 *
 * | Route | reqs/02 | The question it answers |
 * |---|---|---|
 * | `otb` | §2.1 | What is open to spend, in which period, and against which planned terms |
 * | `clusters` | §2.2 | Which doors behave alike, do the schemes actually discriminate, and what does each cluster over-index on |
 * | `size-curves` | §2.5 | What size mix will actually sell, and how much of the raw history was an artefact of stockouts |
 * | `buy` | §2.6 | What are we ordering, from whom, when — and what bound it |
 *
 * The drill targets follow reqs/07 §3.1's object chain rather than convenience. OTB
 * drills to the buy, because the buy is what consumes it. The cluster screen drills to
 * the size curve, because a curve is estimated per cluster and "which cluster is this
 * curve for" is the first question a merchant asks. The size curve drills to the buy,
 * because reqs/02 §8 requires a purchase-order line traceable to the curve that produced
 * it and a screen that could not walk that link would make the trace a query rather than
 * a click.
 *
 * §2.3 assortment and §2.4 NPI are **deliberately not separate routes yet**. Both are
 * Wave 1 and Wave 3 respectively (12 §4.2), and their planner surface is an editing grid
 * over the option plan with live OTB recalculation — which needs the pinned client bumped
 * to a spec carrying `/api/v1/preseason/assortment/price-edit` before it can be anything
 * other than a mock of an edit. The coupling *preview* is on the OTB screen, where it
 * belongs, because that is the number the edit moves.
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than census
 * failures later.
 */

import { defineFeature } from '../manifest';
import { PRESEASON_GUIDES } from './guides';

import { buyExport, clusterExport, otbPlanExport, sizeCurveExport } from './exports';
import {
  ASSORTMENT_BANDS_PATH, ASSORTMENT_PATH, ASSORTMENT_PLAN_PATH, ASSORTMENT_STORE_PATH,
} from './assortment/scope';
import {
  BANDS_EXPORT_ID, bandsExport, LINES_EXPORT_ID, linesExport, MATRIX_EXPORT_ID, matrixExport, ROWS_EXPORT_ID, rowsExport,
} from './assortment/exports';

export default defineFeature({
  slug: 'preseason',
  titleKey: 'preseason.title',
  navLabelKey: 'preseason.navLabel',
  icon: 'clipboard-list',  // brand guide p.14
  railGroup: 'future',  // bata-ui#132 — a chapter's section; the rail's layout does not read it (`docs/adr/9082`)
  localeNamespace: 'preseason',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: PRESEASON_GUIDES,
  // No chapter-level `disabled`: it would grey the whole chapter, the app's page with it
  // (`notOffered()`). Each dormant page says so itself (`docs/adr/9082`).
  navigation: [
    // The app «Surtido» — `RAIL_APPS`, `placement: 'future'` (`docs/adr/9082`): four pages, one function each
    // (ADR 9070 R6). The category analysis, a store, the plan with its «Por qué», and the store groups.
    { labelKey: 'preseason.nav.assortment', to: ASSORTMENT_PATH, order: 19 },
    { labelKey: 'preseason.nav.assortmentStore', to: ASSORTMENT_STORE_PATH, order: 19.1 },
    { labelKey: 'preseason.nav.assortmentPlan', to: ASSORTMENT_PLAN_PATH, order: 19.2 },
    { labelKey: 'preseason.nav.assortmentBands', to: ASSORTMENT_BANDS_PATH, order: 19.3 },
    { labelKey: 'preseason.nav.otb', to: '/preseason/otb', order: 20, disabled: true },
    { labelKey: 'preseason.nav.clusters', to: '/preseason/clusters', order: 21, disabled: true },
    { labelKey: 'preseason.nav.sizeCurves', to: '/preseason/size-curves', order: 22, disabled: true },
    { labelKey: 'preseason.nav.buy', to: '/preseason/buy', order: 23, disabled: true },
  ],
  routes: [
    {
      // Category analysis: the matrix, category × peer group, one measure at a time, with its table view.
      path: 'assortment',
      titleKey: 'preseason.assortment.title',
      // `planRunId`: the plan; `matrixMeasure`, `view`: the grid's measure and the grid or its table; `cluster`: the
      // cluster the matrix is narrowed to; `node`, `peerGroup`: the cell opened (`./assortment/scope.ts`).
      urlParams: ['planRunId', 'matrixMeasure', 'view', 'cluster', 'node', 'peerGroup', 'action', 'store', 'gender', 'brand'],
      component: () => import('./screens/AssortmentScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: MATRIX_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: matrixExport },
      drill: { to: ASSORTMENT_STORE_PATH, grain: 'store', alsoTo: [ASSORTMENT_BANDS_PATH, ASSORTMENT_PLAN_PATH] },
    },
    {
      // One store against its peers: each category's breadth today and the plan's target, the flags, the
      // articles to add and remove, and the decision on each row.
      path: 'assortment/store',
      titleKey: 'preseason.assortment.store.title',
      urlParams: ['planRunId', 'store', 'flag', 'decision', 'action', 'node', 'row', 'gender', 'brand'],
      component: () => import('./screens/AssortmentStoreScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: ROWS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: rowsExport },
      drill: { to: ASSORTMENT_PLAN_PATH, grain: 'article' },
    },
    {
      // The plan's articles with «Por qué», and the plan's acceptance.
      path: 'assortment/plan',
      titleKey: 'preseason.assortment.plan.title',
      urlParams: ['planRunId', 'row', 'store', 'node', 'action', 'gender', 'brand'],
      component: () => import('./screens/AssortmentPlanScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: LINES_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: linesExport },
      drill: { to: ASSORTMENT_STORE_PATH, grain: 'store' },
    },
    {
      // The groups of comparable stores the plan used, their cuts and their stores.
      path: 'assortment/bands',
      titleKey: 'preseason.assortment.bands.pageTitle',
      urlParams: ['planRunId', 'band', 'store'],
      component: () => import('./screens/AssortmentBandsScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: BANDS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: bandsExport },
      drill: { to: ASSORTMENT_STORE_PATH, grain: 'store' },
    },
    {
      path: 'otb',
      titleKey: 'preseason.otb.title',
      urlParams: [],
      component: () => import('./screens/OtbScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'preseason.otb.grid',
        formats: ['csv', 'xlsx'],
        resolve: otbPlanExport,
      },
      drill: { to: '/preseason/buy', grain: 'week', alsoTo: ['/preseason/clusters'] },
    },
    {
      path: 'clusters',
      titleKey: 'preseason.cluster.title',
      // `?row=` — the scheme version the shape and the doors describe (`./scope.ts`).
      urlParams: ['row'],
      component: () => import('./screens/ClusterScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: {
        id: 'preseason.cluster.grid',
        formats: ['csv', 'xlsx'],
        resolve: clusterExport,
      },
      drill: { to: '/preseason/size-curves', grain: 'cluster' },
    },
    {
      path: 'size-curves',
      titleKey: 'preseason.sizeCurve.title',
      // `?row=` — the curve the chart draws (`./scope.ts`).
      urlParams: ['row'],
      component: () => import('./screens/SizeCurveScreen'),
      surfaces: ['chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'preseason.sizeCurve.chart',
        formats: ['csv', 'xlsx'],
        resolve: sizeCurveExport,
      },
      drill: { to: '/preseason/buy', grain: 'sku' },
    },
    {
      path: 'buy',
      titleKey: 'preseason.buy.title',
      // `?week=` — the receipt week the buy depth is derived for (`./scope.ts`).
      urlParams: ['week'],
      component: () => import('./screens/BuyScreen'),
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'preseason.buy.grid',
        formats: ['csv', 'xlsx'],
        resolve: buyExport,
      },
      drill: { to: '/preseason/otb', grain: 'article' },
    },
  ],
});
